import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Broom, Database } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Duplicate-scanner + backfill hub for admins. Scans rooms/tables/menu
 * items/employees/customers/loyalty for duplicates, offers a one-tap
 * clean-up, exposes the idempotent seed backfill (so a fresh prod DB
 * can be brought in line without shell access), and cleans up orphan
 * clock-in shifts left by crashed clients.
 */
const DatabaseHealthCard = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/dedupe?dry_run=true`);
      setReport(res.data);
    } catch (e) {
      toast.error("Scan failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    scan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanUp = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/dedupe`);
      const r = res.data;
      toast.success(
        `Removed ${r.rooms.removed} rooms, ${r.tables.removed} tables, ${r.menu_items.removed} menu items, ${r.users.removed} users, ${r.customers.removed} customers, ${r.loyalty_customers.removed} loyalty members.`
      );
      setReport(r);
      setConfirm(false);
    } catch (e) {
      toast.error("Clean-up failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  const totalDups = report
    ? (report.rooms.removed
        + report.tables.removed
        + report.menu_items.removed
        + report.users.removed
        + report.customers.removed
        + report.loyalty_customers.removed)
    : 0;
  const allClean = report && totalDups === 0;

  return (
    <Card data-testid="database-health-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Database Health</CardTitle>
          <CardDescription>
            Scans for duplicate rooms, tables, menu items, employees, customers and loyalty members.
            Removes extras while keeping the oldest record of each. Foreign-key references on orders,
            shifts and time-clock entries are re-pointed before deletion, and loyalty points from
            duplicate members are merged into the kept record so nothing is lost.
          </CardDescription>
        </div>
        <Button variant="outline" onClick={scan} disabled={loading || running} data-testid="db-rescan-btn">
          {loading ? "Scanning…" : "Rescan"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {!report ? (
          <div className="text-sm text-muted-foreground">Loading database state…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <HealthStat label="Rooms" kept={report.rooms.kept} dup={report.rooms.removed} />
              <HealthStat label="Tables" kept={report.tables.kept} dup={report.tables.removed} />
              <HealthStat label="Menu Items" kept={report.menu_items.kept} dup={report.menu_items.removed} />
              <HealthStat label="Employees" kept={report.users.kept} dup={report.users.removed} />
              <HealthStat label="Customers" kept={report.customers.kept} dup={report.customers.removed} />
              <HealthStat label="Loyalty Members" kept={report.loyalty_customers.kept} dup={report.loyalty_customers.removed} />
            </div>

            {allClean ? (
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800" data-testid="db-clean-banner">
                ✓ All clean. No duplicates detected.
              </div>
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="db-dirty-banner">
                <strong>{totalDups} duplicate{totalDups === 1 ? "" : "s"}</strong> found. Click
                &ldquo;Clean Up Duplicates&rdquo; to remove the extras. The oldest copy of each is kept.
              </div>
            )}

            <div className="flex justify-end gap-2 flex-wrap">
              <Button
                onClick={async () => {
                  try {
                    // /api/seed is idempotent — every insert is guarded
                    // by a find_one, so calling it on a live DB only
                    // backfills missing rows (like the 3 Open Items).
                    const res = await axios.post(`${API}/seed`);
                    const ins = res.data?.inserted || {};
                    const totalNew = Object.values(ins).reduce((s, v) => s + (Number(v) || 0), 0);
                    if (totalNew > 0) {
                      const parts = Object.entries(ins)
                        .filter(([, v]) => Number(v) > 0)
                        .map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`);
                      toast.success(`Backfilled: ${parts.join(", ")}`);
                    } else {
                      toast.success("Everything already in place — nothing to backfill.");
                    }
                  } catch (e) {
                    toast.error("Backfill failed: " + (e.response?.data?.detail || e.message));
                  }
                }}
                variant="outline"
                className="gap-2"
                data-testid="db-backfill-seed-btn"
                title="Idempotent — inserts any missing default rows (Open Food/Drink/Gift Card menu items, default rooms, admin user, etc.) without touching existing data."
              >
                <Database size={18} />
                Backfill Missing Defaults
              </Button>
              <Button
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/timeclock/cleanup-orphans`);
                    const d = res.data;
                    toast.success(
                      `Closed ${d.closed_open_orphans} abandoned shift${d.closed_open_orphans === 1 ? "" : "s"} ` +
                      `and zeroed ${d.fixed_closed_orphans} bogus historical hours.`
                    );
                  } catch (e) {
                    toast.error("Orphan cleanup failed: " + (e.response?.data?.detail || e.message));
                  }
                }}
                variant="outline"
                className="gap-2"
                data-testid="db-clock-cleanup-btn"
              >
                <Broom size={18} />
                Clean Up Orphan Clock-Ins
              </Button>
              <Button
                onClick={() => setConfirm(true)}
                disabled={allClean || running}
                className="gap-2"
                data-testid="db-cleanup-btn"
              >
                <Broom size={18} />
                {running ? "Cleaning…" : "Clean Up Duplicates"}
              </Button>
            </div>
          </>
        )}
      </CardContent>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove duplicates?</DialogTitle>
          </DialogHeader>
          <div className="py-2 text-sm text-muted-foreground space-y-2">
            <p>This will permanently delete:</p>
            <ul className="list-disc pl-5">
              {report?.rooms.removed > 0 && <li>{report.rooms.removed} duplicate room{report.rooms.removed === 1 ? "" : "s"}</li>}
              {report?.tables.removed > 0 && <li>{report.tables.removed} duplicate table{report.tables.removed === 1 ? "" : "s"}</li>}
              {report?.menu_items.removed > 0 && <li>{report.menu_items.removed} duplicate menu item{report.menu_items.removed === 1 ? "" : "s"}</li>}
            </ul>
            <p>The oldest copy of each will be kept. Existing orders linked to surviving records are unaffected.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)} disabled={running}>Cancel</Button>
            <Button onClick={cleanUp} disabled={running} data-testid="db-cleanup-confirm-btn">
              {running ? "Cleaning…" : "Yes, Clean Up"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const HealthStat = ({ label, kept, dup }) => (
  <div className="rounded-lg border border-border p-4 bg-card" data-testid={`db-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="mt-1 flex items-baseline gap-3">
      <span className="text-2xl font-mono font-semibold">{kept}</span>
      <span className="text-xs text-muted-foreground">unique</span>
    </div>
    <div className={`mt-2 text-sm ${dup > 0 ? "text-amber-700 font-medium" : "text-muted-foreground"}`}>
      {dup > 0 ? `${dup} duplicate${dup === 1 ? "" : "s"} to remove` : "No duplicates"}
    </div>
  </div>
);

export default DatabaseHealthCard;
